import Cookies from "js-cookie";
import { getCookieConsentValue } from "react-cookie-consent";

export const COOKIE_CONSENT_NAME = "cookieConsent";
export const COOKIE_PREFS_NAME = "c8_cookie_prefs";

/** Fired on `window` after the privacy modal persists a choice (for pages already mounted). */
export const COOKIE_CONSENT_CHANGED_EVENT = "c8-cookie-consent-changed";

/** Optional cookie categories only — not email marketing (that is account.marketing). */
export type CookieConsentPrefs = {
  analytics: boolean;
  marketing: boolean;
};

const CONSENT_COOKIE_OPTS: Cookies.CookieAttributes = {
  expires: 365,
  path: "/",
  sameSite: "Lax",
};

function onHttps(): boolean {
  return typeof window !== "undefined" && window.location.protocol === "https:";
}

function consentCookieOpts(): Cookies.CookieAttributes {
  return {
    ...CONSENT_COOKIE_OPTS,
    ...(onHttps() ? { secure: true } : {}),
  };
}

/** True when the visitor has already chosen Accept All / Reject All / Accept selected (mixed). */
export function hasCookieConsentDecision(): boolean {
  const value = getCookieConsentValue(COOKIE_CONSENT_NAME);
  return value === "true" || value === "false";
}

/** Stored optional cookie prefs when present; otherwise infer from legacy `cookieConsent`. */
export function readCookieConsentPrefs(): CookieConsentPrefs {
  if (typeof window === "undefined") {
    return { analytics: false, marketing: false };
  }
  try {
    const raw = Cookies.get(COOKIE_PREFS_NAME);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<CookieConsentPrefs>;
      return {
        analytics: parsed.analytics === true,
        marketing: parsed.marketing === true,
      };
    }
  } catch {
    /* ignore */
  }
  const accepted = getCookieConsentValue(COOKIE_CONSENT_NAME) === "true";
  return { analytics: accepted, marketing: accepted };
}

/**
 * Persist cookie-banner choice.
 * `cookieConsent` remains the analytics/GTM gate for existing call sites.
 * `marketing` here is marketing *cookies* only (ads / campaign measurement) — not email.
 */
export function writeCookieConsentPrefs(prefs: CookieConsentPrefs): void {
  const analytics = prefs.analytics === true;
  const marketing = prefs.marketing === true;
  const opts = consentCookieOpts();
  Cookies.set(COOKIE_CONSENT_NAME, analytics ? "true" : "false", opts);
  Cookies.set(COOKIE_PREFS_NAME, JSON.stringify({ analytics, marketing }), opts);
  if (typeof window !== "undefined") {
    window.dispatchEvent(
      new CustomEvent<CookieConsentPrefs>(COOKIE_CONSENT_CHANGED_EVENT, {
        detail: { analytics, marketing },
      })
    );
  }
}

/** True when the user accepted analytics cookies (`cookieConsent`). */
export function hasAnalyticsCookieConsent(): boolean {
  return getCookieConsentValue(COOKIE_CONSENT_NAME) === "true";
}

/** True when marketing cookies (ads / campaign measurement) were accepted. */
export function hasMarketingCookieConsent(): boolean {
  return readCookieConsentPrefs().marketing === true;
}

/** Always include on auth/signup so the server can persist analytics consent for GA4 MP. */
export function analyticsConsentAuthField(): { analytics_consent: boolean } {
  return { analytics_consent: hasAnalyticsCookieConsent() };
}

/** Persist analytics consent to the account when the user is already logged in. */
export function syncAnalyticsConsentToServer(analytics: boolean): void {
  if (typeof window === "undefined") return;
  const apiUrl = process.env.API_URL;
  if (!apiUrl) return;
  try {
    // Lazy import avoids circular deps with authHeaders in some bundles.
    void import("./authHeaders").then(({ getAuthHeaders, getSessionToken, getJwt }) => {
      if (!getSessionToken() && !getJwt()) return;
      void fetch(`${apiUrl}/account/analytics-consent`, {
        method: "POST",
        headers: {
          ...getAuthHeaders(),
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ analytics_consent: analytics === true }),
      }).catch(() => {
        /* ignore — consent is still stored client-side */
      });
    });
  } catch {
    /* ignore */
  }
}
